<template>
  <transition name="edit-summary-modal" appear>
    <div
      v-if="modelValue"
      class="edit-summary-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-summary-modal-title"
      @mousedown.self="onCancel"
    >
      <div class="edit-summary-modal__card">
        <div class="edit-summary-modal__header">
          <div class="edit-summary-modal__header-text">
            <span id="edit-summary-modal-title" class="edit-summary-modal__title">
              Edit Summary
            </span>
            <span class="edit-summary-modal__subtitle">
              {{ subtitle }}
            </span>
          </div>
          <button
            type="button"
            class="edit-summary-modal__close-btn"
            aria-label="Close"
            :disabled="saving"
            @click="onCancel"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <!-- Single-field summary (VIDEO_STEP_BY_STEP_TUTORIAL / migrated single-string) -->
        <div
          v-if="!isTwoPart"
          class="edit-summary-modal__section edit-summary-modal__section--field edit-summary-modal__section--last"
        >
          <MemoInputWithLimit
            :value="localSummary"
            label="Summary"
            :min="0"
            :max="SUMMARY_MAX"
            :rows="5"
            :auto-grow="true"
            :show-char-counter="true"
            :enable-base-validation="false"
            :disabled="saving"
            :status="saving ? 'Disabled' : 'Not focused'"
            custom-width="100%"
            @input="onSummaryInput"
          />
        </div>

        <!-- Two-part summary (VIDEO_WITH_TOC): lead sentence + ordered bullet checklist -->
        <template v-else>
          <div class="edit-summary-modal__section edit-summary-modal__section--field">
            <MemoInputWithLimit
              :value="localSummaryLead"
              label="Lead sentence"
              :min="0"
              :max="SUMMARY_MAX"
              :rows="3"
              :auto-grow="true"
              :show-char-counter="true"
              :enable-base-validation="false"
              :disabled="saving"
              :status="saving ? 'Disabled' : 'Not focused'"
              custom-width="100%"
              @input="onSummaryLeadInput"
            />
          </div>

          <div class="edit-summary-modal__section edit-summary-modal__section--bullets">
            <div class="edit-summary-modal__bullets-heading">
              <span class="edit-summary-modal__bullets-label">Bullet points</span>
              <span class="edit-summary-modal__bullets-hint">
                Short strings shown to learners as a checklist.
              </span>
            </div>

            <div
              v-for="(bullet, index) in localSummaryBullets"
              :key="bullet.key"
              class="edit-summary-modal__bullet-row"
            >
              <div class="edit-summary-modal__bullet-input">
                <MemoInputWithLimit
                  :value="bullet.text"
                  :label="`Bullet ${index + 1}`"
                  :min="0"
                  :max="BULLET_MAX"
                  :rows="2"
                  :auto-grow="true"
                  :show-char-counter="true"
                  :enable-base-validation="false"
                  :disabled="saving"
                  :status="saving ? 'Disabled' : 'Not focused'"
                  custom-width="100%"
                  @input="onBulletInput(index, $event)"
                />
              </div>
              <div class="edit-summary-modal__bullet-actions">
                <button
                  type="button"
                  class="edit-summary-modal__bullet-action"
                  aria-label="Move bullet up"
                  :disabled="saving || index === 0"
                  @click="moveBullet(index, -1)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 8.5L7 4.5L11 8.5"
                      stroke="#344054"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="edit-summary-modal__bullet-action"
                  aria-label="Move bullet down"
                  :disabled="saving || index === localSummaryBullets.length - 1"
                  @click="moveBullet(index, 1)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 5.5L7 9.5L11 5.5"
                      stroke="#344054"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="edit-summary-modal__bullet-action edit-summary-modal__bullet-action--remove"
                  aria-label="Remove bullet"
                  :disabled="saving"
                  @click="removeBullet(index)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
                      stroke="#D92D20"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="button"
              class="edit-summary-modal__add-bullet"
              :disabled="saving"
              @click="addBullet"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M7 3V11M3 7H11"
                  stroke="#6941C6"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
              Add bullet
            </button>
          </div>
        </template>

        <div class="edit-summary-modal__footer">
          <div class="edit-summary-modal__footer-btn">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              :disabled="saving"
              @click="onCancel"
            />
          </div>
          <div class="edit-summary-modal__footer-btn">
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              label="Save"
              :disabled="!isSaveEnabled || saving"
              :loaderLeft="saving"
              @click="onSave"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import MemoInputWithLimit from "@components/MemoInputWithLimit/MemoInputWithLimit.vue";

const SUMMARY_MAX = 500;
const BULLET_MAX = 500;

const props = defineProps({
  /** Controls modal visibility */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** Summary text — pre-filled and editable; max 500 characters */
  summary: {
    type: String,
    default: "",
  },
  /** Two-part (VIDEO_WITH_TOC) mode: lead sentence + ordered bullet list */
  twoPart: {
    type: Boolean,
    default: false,
  },
  /** Lead sentence for the two-part summary shape */
  summaryLead: {
    type: String,
    default: "",
  },
  /** Ordered bullet strings for the two-part summary shape */
  summaryBullets: {
    type: Array,
    default: () => [],
  },
  /** Optional loading state on the Save button */
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "input", "cancel", "save"]);

const isTwoPart = computed(() => props.twoPart === true);

const subtitle = computed(() =>
  isTwoPart.value
    ? "Update the lead sentence and bullet points shown to learners for this video."
    : "Update the summary shown to learners for this video."
);

function normalizeBulletTexts(bullets) {
  if (!Array.isArray(bullets)) return [];
  return bullets.map((bullet) => String(bullet ?? ""));
}

let bulletKeySeq = 0;

function nextBulletKey() {
  bulletKeySeq += 1;
  return `edit-summary-bullet-${bulletKeySeq}`;
}

/**
 * Bullets are held as `{ key, text }` rows: the rows can be reordered, added to
 * and removed, so the array index is not a stable identity for the `v-for` key
 * and reusing it makes Vue patch a row's input onto the wrong bullet.
 */
function toBulletRows(bullets) {
  return normalizeBulletTexts(bullets).map((text) => ({ key: nextBulletKey(), text }));
}

const localSummary = ref(props.summary || "");
const localSummaryLead = ref(props.summaryLead || "");
const localSummaryBullets = ref(toBulletRows(props.summaryBullets));

const bulletTexts = computed(() => localSummaryBullets.value.map((bullet) => bullet.text));

const isDirty = computed(() => {
  if (isTwoPart.value) {
    return (
      localSummaryLead.value !== (props.summaryLead || "") ||
      JSON.stringify(bulletTexts.value) !==
        JSON.stringify(normalizeBulletTexts(props.summaryBullets))
    );
  }
  return localSummary.value !== (props.summary || "");
});

const hasRequiredContent = computed(() => {
  if (isTwoPart.value) {
    return (
      localSummaryLead.value.trim().length > 0 ||
      localSummaryBullets.value.some((bullet) => bullet.text.trim().length > 0)
    );
  }
  return localSummary.value.trim().length > 0;
});

/** Allow typing past max — input shows its error; Save stays off until fixed */
const isWithinLimits = computed(() => {
  if (isTwoPart.value) {
    const leadWithinLimit = localSummaryLead.value.length <= SUMMARY_MAX;
    const bulletsWithinLimit = localSummaryBullets.value.every(
      (bullet) => bullet.text.length <= BULLET_MAX
    );
    return leadWithinLimit && bulletsWithinLimit;
  }
  return localSummary.value.length <= SUMMARY_MAX;
});

const isSaveEnabled = computed(
  () => isDirty.value && hasRequiredContent.value && isWithinLimits.value
);

function syncFromProps() {
  localSummary.value = props.summary || "";
  localSummaryLead.value = props.summaryLead || "";
  localSummaryBullets.value = toBulletRows(props.summaryBullets);
}

function onSummaryInput(value) {
  localSummary.value = value || "";
}

function onSummaryLeadInput(value) {
  localSummaryLead.value = value || "";
}

function onBulletInput(index, value) {
  // Replace the array instead of assigning by index: Vue 2 does not track
  // `arr[i] = x`, so an in-place bullet edit would not re-evaluate isDirty and
  // Save would stay disabled for a bullets-only edit. The row keeps its key so
  // the edited row is not remounted (and its input not re-patched) on save.
  const bullets = [...localSummaryBullets.value];
  bullets[index] = { ...bullets[index], text: value || "" };
  localSummaryBullets.value = bullets;
}

function addBullet() {
  localSummaryBullets.value.push({ key: nextBulletKey(), text: "" });
}

function removeBullet(index) {
  localSummaryBullets.value.splice(index, 1);
}

function moveBullet(index, direction) {
  const bullets = localSummaryBullets.value;
  const target = index + direction;
  if (target < 0 || target >= bullets.length) return;
  const next = [...bullets];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  localSummaryBullets.value = next;
}

function closeModal() {
  emit("update:modelValue", false);
  emit("input", false);
}

function onCancel() {
  if (props.saving) return;
  syncFromProps();
  closeModal();
  emit("cancel");
}

function onSave() {
  if (!isSaveEnabled.value || props.saving) return;
  if (isTwoPart.value) {
    emit("save", {
      summary_lead: localSummaryLead.value.trim(),
      summary_bullets: localSummaryBullets.value
        .map((bullet) => bullet.text.trim())
        .filter((bullet) => bullet.length > 0),
    });
    return;
  }
  emit("save", {
    summary: localSummary.value,
  });
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) syncFromProps();
  }
);
</script>

<style scoped>
.edit-summary-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.edit-summary-modal__card {
  width: 520px;
  max-width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #e4e4e4;
  background-color: #ffffff;
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0 0;
  background-repeat: no-repeat;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  display: flex;
  flex-direction: column;
}

.edit-summary-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #e4e4e4;
}

.edit-summary-modal__header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.edit-summary-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.edit-summary-modal__subtitle {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.edit-summary-modal__close-btn {
  appearance: none;
  flex-shrink: 0;
  background: transparent;
  border: 0;
  padding: 4px;
  margin: 0;
  cursor: pointer;
  line-height: 0;
}

.edit-summary-modal__close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.edit-summary-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
  border-radius: 8px;
}

.edit-summary-modal__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 24px;
  border-bottom: 1px solid #e4e4e4;
}

.edit-summary-modal__section--field {
  background-color: #f9fafb;
}

.edit-summary-modal__section--last {
  border-bottom: 0;
}

.edit-summary-modal__section--bullets {
  background-color: #f9fafb;
  border-bottom: 0;
}

.edit-summary-modal__bullets-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.edit-summary-modal__bullets-label {
  color: #101828;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.edit-summary-modal__bullets-hint {
  color: #475467;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.edit-summary-modal__bullet-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.edit-summary-modal__bullet-input {
  flex: 1;
  min-width: 0;
}

.edit-summary-modal__bullet-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  padding-top: 24px;
}

.edit-summary-modal__bullet-action {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  margin: 0;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  line-height: 0;
}

.edit-summary-modal__bullet-action:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #bfc8d4;
}

.edit-summary-modal__bullet-action:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.edit-summary-modal__bullet-action:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.edit-summary-modal__bullet-action--remove:hover:not(:disabled) {
  background-color: #fef3f2;
  border-color: #fda29b;
}

.edit-summary-modal__add-bullet {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  align-self: flex-start;
  padding: 8px 12px;
  margin: 4px 0 0;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  background-color: #ffffff;
  color: #6941c6;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
}

.edit-summary-modal__add-bullet:hover:not(:disabled) {
  background-color: #f4ebff;
  border-color: #b692f6;
}

.edit-summary-modal__add-bullet:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.edit-summary-modal__add-bullet:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.edit-summary-modal__footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 24px 24px;
  border-top: 1px solid #e4e4e4;
  background-color: #ffffff;
  box-sizing: border-box;
}

.edit-summary-modal__footer-btn {
  flex: 1;
  min-width: 0;
}

.edit-summary-modal-enter-active,
.edit-summary-modal-leave-active {
  transition: opacity 180ms ease;
}

.edit-summary-modal-enter-from,
.edit-summary-modal-leave-to {
  opacity: 0;
}

.edit-summary-modal-enter-active .edit-summary-modal__card,
.edit-summary-modal-leave-active .edit-summary-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
  will-change: transform, opacity;
}

.edit-summary-modal-enter-from .edit-summary-modal__card,
.edit-summary-modal-leave-to .edit-summary-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

/* Shared inputs bring their own min-width; keep modal fields fluid */
.edit-summary-modal__section ::v-deep(.memo_input) {
  min-width: 0;
}

/* Keep textarea surface white against the grey section fill */
.edit-summary-modal__section--field ::v-deep(.v-input__slot) {
  background-color: #ffffff !important; /* override shared input slot fill on grey section */
}

.edit-summary-modal__section--bullets ::v-deep(.v-input__slot) {
  background-color: #ffffff !important; /* override shared input slot fill on grey section */
}

/* Auto-grow: expand with content, no inner scrollbar */
.edit-summary-modal__section--field ::v-deep(textarea) {
  overflow: hidden !important; /* hide scrollbar; height grows via auto-grow */
  resize: none;
}

.edit-summary-modal__section--bullets ::v-deep(textarea) {
  overflow: hidden !important;
  resize: none;
}
</style>
